import { useEffect, useRef, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";

const NAV_ITEMS = [
  { key: "about", href: "#about" },
  { key: "services", href: "#services" },
  { key: "projects", href: "#projects" },
  { key: "team", href: "#team" },
  { key: "insights", href: "#insights" },
  { key: "faq", href: "#faq" },
  { key: "contact", href: "#contact" },
];

export default function Navigation() {
  const { t, lang, setLang, languages, isRtl } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!langOpen) return;
    const onClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setLangOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [langOpen]);

  const scrollTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <header
        data-testid="site-navigation"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "nav-glass" : ""}`}
      >
        <div className="container-lux flex items-center justify-between py-4">
          <button
            onClick={() => scrollTo("#top")}
            className="flex items-center gap-3"
            data-testid="nav-logo"
            aria-label="TA CLÉ INVEST — Home"
          >
            <Logo size={68} />
          </button>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.key}
                onClick={() => scrollTo(item.href)}
                className="nav-link"
                data-testid={`nav-link-${item.key}`}
              >
                {t.nav[item.key]}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-6">
            <div ref={langRef} className="relative" role="group" aria-label="Language">
              <button
                type="button"
                onClick={() => setLangOpen((v) => !v)}
                className="lang-trigger"
                aria-haspopup="listbox"
                aria-expanded={langOpen}
                data-testid="lang-trigger"
              >
                <span className="lang-trigger-code">{languages.find((l) => l.code === lang)?.label}</span>
                <ChevronDown
                  size={12}
                  strokeWidth={1.5}
                  className={`transition-transform duration-300 ${langOpen ? "rotate-180" : ""}`}
                />
              </button>

              <div
                className={`lang-menu ${langOpen ? "open" : ""}`}
                role="listbox"
                data-testid="lang-menu"
                aria-hidden={!langOpen}
              >
                {languages.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                    className={`lang-menu-item ${lang === l.code ? "active" : ""}`}
                    data-testid={`lang-switch-${l.code}`}
                    role="option"
                    aria-selected={lang === l.code}
                  >
                    <span className="lang-menu-code">{l.label}</span>
                    <span className="lang-menu-name">{l.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => scrollTo("#contact")}
              className="btn-primary"
              data-testid="nav-cta-button"
            >
              {t.nav.cta}
            </button>
          </div>

          <button
            onClick={() => setOpen(true)}
            className="lg:hidden p-2 -m-2"
            data-testid="mobile-menu-open"
            aria-label="Open menu"
          >
            <Menu size={22} strokeWidth={1.2} />
          </button>
        </div>
      </header>

      <div className={`mobile-menu ${open ? "open" : ""} lg:hidden`} data-testid="mobile-menu" aria-hidden={!open}>
        <button
          onClick={() => setOpen(false)}
          className={`absolute top-6 ${isRtl ? "left-6" : "right-6"} p-2 -m-2`}
          data-testid="mobile-menu-close"
          aria-label="Close menu"
        >
          <X size={24} strokeWidth={1.2} />
        </button>

        <div className="flex flex-col gap-2">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.key}
              onClick={() => scrollTo(item.href)}
              className="text-start py-4 border-b border-[color:var(--line-strong)] font-serif-display text-3xl"
              data-testid={`mobile-nav-${item.key}`}
            >
              {t.nav[item.key]}
            </button>
          ))}
        </div>

        <div className="mt-10">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[color:var(--copper)] mb-4">
            Language
          </div>
          <div className="flex flex-col gap-3" data-testid="mobile-lang-column">
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => { setLang(l.code); }}
                className={`flex items-center gap-3 text-start py-1 ${lang === l.code ? "text-[color:var(--copper)]" : "text-[color:var(--anthracite)]/70"}`}
                data-testid={`mobile-lang-${l.code}`}
              >
                <span className="font-serif-display text-[18px] w-8">{l.label}</span>
                <span className="text-[11px] tracking-[0.22em] uppercase">{l.name}</span>
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={() => scrollTo("#contact")}
          className="btn-primary mt-10 w-full justify-center"
          data-testid="mobile-cta-button"
        >
          {t.nav.cta}
        </button>
      </div>
    </>
  );
}
